import ajaxRequest from '../../../helpers/ajaxRequest';
import removeChildNodes from '../../../helpers/removeChildNodes';
import isTouchSupported from '../../../helpers/isTouchSupported';
import animateMiniPopup from './animateMiniPopup';
import { API, MiniCartEntry, MiniCartData, ImageTransformation } from '../../../types';

interface InitMiniCart {
  fetchInitialMiniCart: () => Promise<void>;
  initMiniCartTouchHoverEvents: (isXLargeUp: boolean) => void;
  cleanUpMiniCartOnViewportResize: () => void;
  updateMiniCartGlobalState: (miniCartData: Omit<MiniCartData, 'subtotal'>) => void;
  updateMiniCartMarkup: (miniCartData: MiniCartData) => void;
  updateMiniCartPopupMarkup: ({
    entry,
    subtotal,
    isError,
    errorMessage
  }: {
    entry?: MiniCartEntry;
    subtotal?: string;
    isError?: boolean;
    errorMessage?: string;
  }) => void;
}

function initMiniCart(): InitMiniCart {
  const HIDE = 'hide';
  const HIDE_VISIBILITY = 'hide-visibility';

  const miniCartRootElement = document.querySelector<HTMLLIElement>('.page-header__icons-item--cart');
  const miniCartLinkElement = miniCartRootElement?.querySelector<HTMLAnchorElement>('.page-header__icons-item-link');
  const miniCartElement = miniCartRootElement?.querySelector<HTMLDivElement>('.mini-cart');

  const miniCartBadgeElements = document.querySelectorAll<HTMLSpanElement>('[data-cart-item-counter]');
  const miniCartEmptyIconElement = miniCartRootElement?.querySelector<SVGAElement>('.icon-shopping-bag');
  const miniCartFullIconElement = miniCartRootElement?.querySelector<SVGAElement>('.icon-bag-full');
  const miniCartProductsElement = document.querySelector<HTMLDivElement>('.mini-cart__products');
  const miniCartProductsListElement = document.querySelector<HTMLUListElement>('.mini-cart__products-list');
  const miniCartEmptyListElement = document.querySelector<HTMLDivElement>('.mini-cart__empty');
  const miniCartRemainingElement = document.querySelector<HTMLParagraphElement>('.mini-cart__products-remaining');
  const miniCartRemainingCounterElement = miniCartRemainingElement?.querySelector<HTMLSpanElement>('span');
  const miniCartSubtotalValueElement = document.querySelector<HTMLElement>('.mini-cart__products-subtotal-value');

  const miniCartPopupElement = document.querySelector<HTMLDivElement>('.mini-cart-popup');
  const miniCartPopupHeadingElement = document.querySelector<HTMLDivElement>('.mini-cart-popup__heading');
  const miniCartPopupProductElement = document.querySelector<HTMLDivElement>('.mini-cart-popup__product');
  const miniCartPopupErrorElement = document.querySelector<HTMLDivElement>('.mini-cart-popup__error');
  const miniCartPopupErrorMessageElement = document.querySelector<HTMLParagraphElement>(
    '.mini-cart-popup__error-message'
  );
  const miniCartPopupErrorMessageGenericElement = document.querySelector<HTMLParagraphElement>(
    '.mini-cart-popup__error-message--generic'
  );
  const miniCartPopupCallToActionElement = document.querySelector<HTMLDivElement>('.mini-cart-popup__call-to-action');
  const miniCartPopupSubtotalValueElement = document.querySelector<HTMLElement>(
    '.mini-cart-popup__call-to-action-subtotal-value'
  );

  function updateMiniCartGlobalState({
    entries,
    miniCartDisplayLimit,
    totalEntryQuantities
  }: Omit<MiniCartData, 'subtotal'>): void {
    window.bbGlobal.miniCartData.entries = entries;
    window.bbGlobal.miniCartData.miniCartDisplayLimit = miniCartDisplayLimit;
    window.bbGlobal.miniCartData.totalEntryQuantities = totalEntryQuantities;
  }

  function updateMiniCartBadgeMarkup(totalEntryQuantities: number): void {
    [...miniCartBadgeElements].forEach((badgeElement) => {
      badgeElement.textContent = `${totalEntryQuantities}`;
      badgeElement.classList[totalEntryQuantities > 0 ? 'remove' : 'add'](HIDE);
      if (totalEntryQuantities >= 100) {
        badgeElement.classList.add(HIDE);
      }
    });
  }

  function updateMiniCartIconMarkup(totalEntryQuantities: number): void {
    miniCartEmptyIconElement?.classList[!!totalEntryQuantities === false ? 'remove' : 'add'](HIDE);
    miniCartFullIconElement?.classList[totalEntryQuantities > 0 ? 'remove' : 'add'](HIDE);
  }

  function updateMiniCartProductsMarkup(entries: MiniCartEntry[]): void {
    removeChildNodes(miniCartProductsListElement);
    entries.forEach((entry) => {
      const listItem = document.createElement('li');
      listItem.classList.add('products-list-item');

      if (entry.productUnavailable) {
        listItem.classList.add('products-list-item--unavailable');
      }

      const listItemLink = document.createElement('a');
      listItemLink.classList.add('products-list-item-link');
      listItemLink.href = entry.productUrl;

      const listItemLinkImage = document.createElement('img');
      listItemLinkImage.classList.add('products-list-item-link-image');
      listItemLinkImage.src = entry.productImage;
      listItemLinkImage.srcset = `${entry.productImage}?$${ImageTransformation.LOUNHMC}$ 1x, ${entry.productImage}?$${ImageTransformation.LOUNHMC_R}$ 2x`;
      listItemLinkImage.alt = '';

      const listItemInfo = document.createElement('div');
      listItemInfo.classList.add('products-list-item-info');

      const listItemInfoManufacturerContainer = document.createElement('div');
      listItemInfoManufacturerContainer.classList.add('products-list-item-info-manufacturer-container');

      const [manufacturer, productName] = entry.productTitle.split('&#32;');

      const listItemInfoManufacturer = document.createElement('p');
      listItemInfoManufacturer.classList.add('products-list-item-info-manufacturer');
      listItemInfoManufacturer.innerHTML = manufacturer;

      const listItemInfoQuantity = document.createElement('p');
      listItemInfoQuantity.classList.add('products-list-item-info-quantity');
      if (entry.productQuantity > 1) {
        listItemInfoQuantity.textContent = `Qty ${entry.productQuantity}`;
      }

      const listItemInfoTitle = document.createElement('p');
      listItemInfoTitle.classList.add('small-text', 'products-list-item-info-title');
      listItemInfoTitle.innerHTML = productName;

      const listItemInfoVariants = entry.productVariants.map((productVariant) => {
        const listItemInfoVariant = document.createElement('p');
        listItemInfoVariant.classList.add('small-text', 'products-list-item-info-variant');
        listItemInfoVariant.textContent = productVariant;
        return listItemInfoVariant;
      });

      const listItemInfoUnavailableMessage = document.createElement('p');
      listItemInfoUnavailableMessage.classList.add('small-text', 'products-list-item-info-unavailable-message');
      listItemInfoUnavailableMessage.textContent = window.bbGlobal.miniCartData.unavailableMessage;

      const listItemInfoPrices = document.createElement('ul');
      listItemInfoPrices.classList.add('products-list-item-info-prices');

      if (entry.productWasWasPrice) {
        const listItemInfoWasWasPrice = document.createElement('p');
        listItemInfoWasWasPrice.classList.add('small-text', 'products-list-item-info-prices-was-price');
        listItemInfoWasWasPrice.textContent = entry.productWasWasPrice;
        listItemInfoPrices.appendChild(listItemInfoWasWasPrice);
      }

      if (entry.productWasPrice) {
        const listItemInfoWasPrice = document.createElement('p');
        listItemInfoWasPrice.classList.add('small-text', 'products-list-item-info-prices-was-price');
        listItemInfoWasPrice.textContent = entry.productWasPrice;
        listItemInfoPrices.appendChild(listItemInfoWasPrice);
      }

      const listItemInfoPrice = document.createElement('p');
      listItemInfoPrice.classList.add('small-text', 'products-list-item-info-prices-price');
      if (entry.productDiscounted) {
        listItemInfoPrice.classList.add('products-list-item-info-prices-price--discounted');
      }

      listItemInfoPrice.textContent = entry.productPrice;
      listItemInfoPrices.appendChild(listItemInfoPrice);

      listItem.appendChild(listItemLink);
      listItemLink.appendChild(listItemLinkImage);
      listItem.appendChild(listItemInfo);
      listItemInfo.appendChild(listItemInfoManufacturerContainer);
      listItemInfoManufacturerContainer.appendChild(listItemInfoManufacturer);
      listItemInfoManufacturerContainer.appendChild(listItemInfoQuantity);
      listItemInfo.appendChild(listItemInfoTitle);
      listItemInfoVariants.forEach((listItemInfoVariant) => listItemInfo.appendChild(listItemInfoVariant));
      listItemInfo.appendChild(listItemInfoUnavailableMessage);
      listItemInfo.appendChild(listItemInfoPrices);
      miniCartProductsListElement?.appendChild(listItem);
    });
  }

  function updateMiniCartRemainingProductsMarkup(totalEntries: number): void {
    const remainingEntries = totalEntries - window.bbGlobal.miniCartData.maxMiniLimit;
    if (miniCartRemainingElement && miniCartRemainingCounterElement) {
      miniCartRemainingElement.classList[remainingEntries > 0 ? 'remove' : 'add'](HIDE);
      miniCartRemainingCounterElement.textContent = `${remainingEntries}`;
    }
  }

  function updateMiniCartSubtotalMarkup(subtotal: string): void {
    if (miniCartSubtotalValueElement) {
      miniCartSubtotalValueElement.textContent = subtotal;
    }
  }

  function updateMiniCartMarkup({ entries, miniCartDisplayLimit, totalEntryQuantities, subtotal }: MiniCartData): void {
    updateMiniCartIconMarkup(totalEntryQuantities);
    updateMiniCartBadgeMarkup(totalEntryQuantities);
    updateMiniCartProductsMarkup(entries.reverse().slice(0, miniCartDisplayLimit));
    updateMiniCartRemainingProductsMarkup(entries.length);
    updateMiniCartSubtotalMarkup(subtotal);
    miniCartProductsElement?.classList[entries.length > 0 ? 'remove' : 'add'](HIDE);
    miniCartEmptyListElement?.classList[entries.length > 0 ? 'add' : 'remove'](HIDE);
  }

  function updateMiniCartPopupMarkup({
    entry,
    subtotal = '',
    isError = false,
    errorMessage = ''
  }: {
    entry?: MiniCartEntry;
    subtotal?: string;
    isError?: boolean;
    errorMessage?: string;
  }): void {
    removeChildNodes(miniCartPopupProductElement);
    miniCartPopupHeadingElement?.classList[isError ? 'add' : 'remove'](HIDE);
    miniCartPopupProductElement?.classList[isError ? 'add' : 'remove'](HIDE);
    miniCartPopupErrorElement?.classList[isError ? 'remove' : 'add'](HIDE);
    miniCartPopupCallToActionElement?.classList[isError ? 'add' : 'remove'](HIDE);
    if (isError && miniCartPopupErrorMessageElement) {
      miniCartPopupErrorMessageElement.innerHTML = errorMessage;
      miniCartPopupErrorMessageElement.classList[errorMessage ? 'remove' : 'add'](HIDE);
      miniCartPopupErrorMessageGenericElement?.classList[errorMessage ? 'add' : 'remove'](HIDE);
    }

    if (!isError && entry) {
      const productImage = document.createElement('img');
      productImage.classList.add('mini-cart-popup__product-image');
      productImage.src = entry.productImage;
      productImage.srcset = `${entry.productImage}?$${ImageTransformation.LOUNHMC}$ 1x, ${entry.productImage}?$${ImageTransformation.LOUNHMC_R}$ 2x`;
      productImage.alt = '';
      const productInfo = document.createElement('div');
      productInfo.classList.add('mini-cart-popup__product-info');
      const productBrand = document.createElement('p');
      productBrand.classList.add('mini-cart-popup__product-brand');
      productBrand.innerHTML = entry.manufacturer;
      const productTitle = document.createElement('p');
      productTitle.classList.add('mini-cart-popup__product-title');
      productTitle.innerHTML = entry.name;
      const productVariants = entry.productVariants.map((productVariant) => {
        const productVariantElement = document.createElement('p');
        productVariantElement.classList.add('mini-cart-popup__product-variants');
        productVariantElement.textContent = productVariant;
        return productVariantElement;
      });

      const productPrice = document.createElement('p');
      productPrice.classList.add('mini-cart-popup__product-price');
      productPrice.textContent = entry.productPrice;
      miniCartPopupProductElement?.appendChild(productImage);
      productInfo?.appendChild(productBrand);
      productInfo?.appendChild(productTitle);
      productVariants.forEach((productVariant) => productInfo?.appendChild(productVariant));
      productInfo?.appendChild(productPrice);
      if (miniCartPopupSubtotalValueElement) {
        miniCartPopupSubtotalValueElement.textContent = subtotal;
      }

      miniCartPopupProductElement?.appendChild(productInfo);
    }
    animateMiniPopup(miniCartPopupElement, miniCartRootElement);
  }

  function fetchInitialMiniCart(): Promise<void> {
    window.bbGlobal.miniCartData.isDataFetching = true;
    return ajaxRequest({ method: 'GET', url: API.CART_ROLLOVER })
      .then((miniCartData) => {
        updateMiniCartGlobalState(miniCartData);
        updateMiniCartMarkup(miniCartData);
        window.bbGlobal.miniCartData.isInitialDataFetched = true;
        window.bbGlobal.miniCartData.isDataFetching = false;
      })
      .catch(() => {
        window.bbGlobal.miniCartData.isDataFetching = false;
        console.error('Something went wrong in the fetch');
      });
  }

  function miniCartHoverHandler(): void {
    if (
      miniCartElement?.classList.contains(HIDE_VISIBILITY) &&
      miniCartPopupElement?.classList.contains(HIDE) &&
      window.bbGlobal.miniCartData.isInitialDataFetched &&
      window.bbGlobal.miniCartData.isDataFetching === false
    ) {
      showMiniCart();
      document.addEventListener('mousemove', function hideOnOutsideHover(event: MouseEvent) {
        if ((event.target as Element).closest('.page-header__icons-item--cart') === null) {
          hideMiniCart();
          document.removeEventListener('mousemove', hideOnOutsideHover);
        }
      });
    }
  }

  function miniCartTouchHandler(e: TouchEvent): void {
    if (
      e.touches.length === 1 &&
      miniCartElement?.classList.contains(HIDE_VISIBILITY) &&
      miniCartPopupElement?.classList.contains(HIDE) &&
      window.bbGlobal.miniCartData.isInitialDataFetched &&
      window.bbGlobal.miniCartData.isDataFetching === false
    ) {
      e.preventDefault();
      showMiniCart();
      document.addEventListener(
        'touchstart',
        function hideOnOutsideTouch(event: TouchEvent) {
          if ((event.target as HTMLElement).closest('.page-header__icons-item--cart') === null) {
            event.stopPropagation();
            event.preventDefault();
            hideMiniCart();
            document.removeEventListener('touchstart', hideOnOutsideTouch);
          }
        },
        { passive: false }
      );
    }
  }

  function showMiniCart(): void {
    miniCartElement?.classList.remove(HIDE_VISIBILITY);
  }

  function hideMiniCart(): void {
    miniCartElement?.classList.add(HIDE_VISIBILITY);
  }

  function initMiniCartTouchHoverEvents(isXLargeUp: boolean): void {
    if (isXLargeUp) {
      miniCartLinkElement?.addEventListener('mouseover', miniCartHoverHandler);
      if (isTouchSupported()) {
        miniCartLinkElement?.addEventListener('touchstart', miniCartTouchHandler);
      }
    } else {
      miniCartLinkElement?.removeEventListener('mouseover', miniCartHoverHandler);
      if (isTouchSupported()) {
        miniCartLinkElement?.removeEventListener('touchstart', miniCartTouchHandler);
      }
    }
  }

  function cleanUpMiniCartOnViewportResize(): void {
    hideMiniCart();
  }

  return {
    fetchInitialMiniCart,
    initMiniCartTouchHoverEvents,
    cleanUpMiniCartOnViewportResize,
    updateMiniCartGlobalState,
    updateMiniCartMarkup,
    updateMiniCartPopupMarkup
  };
}

export default initMiniCart;
